import { filter, get, isEmpty, isNull, keyBy, omit, startCase } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

import {
  Checkbox,
  FormControlLabel,
  Grid,
  Input,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Divider from '@mui/material/Divider';
import { Badge, Button, If } from '@fashionnovateam/nova-ui-toolkit';
import ApproveReject from '../ApproveReject/ApproveReject';
import FileInput from '../FileInput/FileInput';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';

import CountryList from '../../Lib/Constants/countries';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';

import { isValidPhone } from '../../Lib/Utils/FormUtils/vendor-application';
import validateEmail from '../../Lib/Utils/Helpers/validate-email';
import makeFactoryStatus from '../../Lib/Utils/Factory/make-factory-status';
import isDomestic from '../../Lib/Utils/VendorApplication/is-domestic';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import toast from '../Toast/Toast';

const useStyles = makeStyles()((theme) => ({
  auditExpirationDate: {},
  auditExpirationDateLabel: {},
  checkboxLabelContainer: {
    width: '100%',
  },
  checkboxLabel: {
    fontWeight: 'bold',
    flex: '1',
    margin: '0px',
  },
  error: {
    color: get(theme, 'palette.destructive.main'),
  },
  form: {
    width: '100%',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    margin: '15px 0px',
  },
  sectionHeader: {
    marginTop: '25px',
  },
}));

const requiredFields = keyBy([
  'legal_name',
  'address',
  'city',
  'zip',
  'country',
  'contact_name',
  'contact_email',
  'contact_phone',
  'garment_registration_number',
  'auditDocument',
  'factoryAgreement',
]);

const emptyFactory = {
  legal_name: '',
  address: '',
  address_2: '',
  city: '',
  zip: '',
  state: '',
  country: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  garment_registration_number: '',
  audit_expiration_date: null,
  auditDocument: null,
  factoryAgreement: null,
};

export default function VendorFactory(props) {
  const { currentUser } = props;
  const perms = useMemo(() => {
    const canCreate = hasPermissionTo(
      'users/:id/factories',
      currentUser.role,
      Actions.CREATE,
      'FACTORY',
    );
    const canUpdate = hasPermissionTo(
      'users/:id/factories',
      currentUser.role,
      Actions.UPDATE,
      'FACTORY',
    );
    const canAcceptReject = hasPermissionTo(
      'users/:id/factories',
      currentUser.role,
      Actions.UPDATE,
      'ACCEPT_REJECT',
    );

    return {
      canCreate,
      canUpdate,
      canAcceptReject,
    };
  }, [currentUser]);

  const [factory, setFactory] = useState({
    ...emptyFactory,
    vendor_internal_id: props.user.vendor.internal_id,
  });
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const { classes } = useStyles();
  const disableForm =
    (!factory.rejection_reason &&
      !!factory.submitted_at &&
      !factory.approved_at) ||
    (factory.id && !perms.canUpdate) ||
    (!factory.id && !perms.canCreate);

  useEffect(() => {
    const { factoryId } = props.match.params;
    if (!factoryId || factoryId === 'new') return;

    props
      .getFactory(props.match.params.factoryId, props.match.params.id)
      .then((r) => setFactory(r.factory))
      .catch((err) => setError(err));
  }, [props.match.params.factoryId]);

  function create(f) {
    return props
      .createFactory(f, props.user.id)
      .then(() => {
        props.history.push(`/users/${props.user.id}/factories`);
      })
      .catch(setError);
  }

  function update(updates) {
    return props
      .updateFactory(updates, factory.id, props.user.id)
      .then(() => {
        props.history.push(`/users/${props.user.id}/factories`);
      })
      .catch(setError);
  }

  const submit = () => {
    const domestic = isDomestic(factory.country);
    const auditDocRequired =
      get(props, `user.vendor.is_direct`) ||
      get(props, `user.vendor.merch_type`) === 'Direct' ||
      domestic;

    const missingFields = filter(requiredFields, (f) => {
      if (f === 'factoryAgreement') return isNull(factory[f]);
      if (f === 'auditDocument') {
        return auditDocRequired ? isNull(factory[f]) : false;
      }

      return isEmpty(factory[f]);
    });

    if (!isEmpty(missingFields)) {
      setFormErrors((f) => ({
        ...f,
        ...missingFields.reduce(
          (errs, field) => ({ ...errs, [field]: true }),
          {},
        ),
      }));
      setSubmitError(
        `The following fields must be completed: ${missingFields
          .map(startCase)
          .join(', ')}`,
      );

      return;
    }

    const method = factory.id ? update : create;

    method(
      omit(
        {
          ...factory,
          submitted_at: new Date(),
          rejection_reason: null,
          rejected_at: null,
          approved_by: null,
        },
        ['approved_at'],
      ),
    );
  };

  const approve = () => {
    const updates = {
      approved_by: props.currentUser.id,
      audit_expiration_date: factory.audit_expiration_date,
    };

    if (!factory.approved_at) {
      updates.approved_at = new Date().toISOString();
    }

    update(updates);
  };

  function reject({ rejection_reason }) {
    update({ rejection_reason, approved_by: null });
  }

  const onChange = (e) => {
    e.persist();
    setFactory((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onChangeFile = (file, fileName) => {
    if (!factory.id) {
      setFactory((f) => ({ ...f, [fileName]: file }));
      return;
    }

    props
      .updateFile(file, get(factory, `${fileName}.write`), fileName)
      .catch((err) => {
        console.error('Error uploading file', err);
        toast.error('Error uploading file');
      })
      .then(() => {
        props
          .getFactory(props.match.params.factoryId, props.match.params.id)
          .then((r) => setFactory(r.factory))
          .catch((err) => setError(err));
      });
  };

  const isUpdateForm = Boolean(factory.id);
  const factoryStatus = makeFactoryStatus(factory);

  return (
    <Page.Container withoutTwp>
      <Header.Major
        icon="building/building-apartment-one"
        title="Factories"
        subTitle={factory.id || 'Add New'}
        onCloseLink={`/users/${props.user.id}/factories`}
      >
        <If condition={factory.id}>
          {factory.rejection_reason ? (
            <Badge color="red">
              <b>{factoryStatus}: </b> {factory.rejection_reason}
            </Badge>
          ) : (
            <Badge
              color={factoryStatus === 'Approved' ? 'green' : 'neutral'}
              contrast="low"
            >
              {factoryStatus}
            </Badge>
          )}
        </If>
      </Header.Major>

      <Page.Content>
        <span className="text-xl font-semibold">
          {factory.id || 'ADD NEW FACTORY'}
        </span>
        <form className={classes.form}>
          <div className={classes.row}>
            <TextInput
              disabled={disableForm}
              fullWidth
              required
              label="Factory Legal Name"
              value={factory.legal_name}
              error={formErrors.legal_name}
              name="legal_name"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  legal_name: isEmpty(factory.legal_name),
                }))
              }
            />
          </div>

          <Typography
            variant="h5"
            className={classes.sectionHeader}
            gutterBottom
          >
            Factory Address
          </Typography>
          <Divider />

          <div className={classes.row}>
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Address"
              value={factory.address}
              name="address"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  address: isEmpty(factory.address),
                }))
              }
            />
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Address 2"
              value={factory.address2}
              name="address_2"
              onChange={onChange}
            />
          </div>
          <div className={classes.row}>
            <TextInput
              disabled={disableForm}
              fullWidth
              label="City"
              value={factory.city}
              name="city"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  city: isEmpty(factory.city),
                }))
              }
            />
            <TextInput
              fullWidth
              disabled={disableForm}
              label="State, Province or Region"
              value={factory.state}
              name="state"
              onChange={onChange}
            />
          </div>
          <div className={classes.row}>
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Zip or Postal Code"
              value={factory.zip}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  zip: isEmpty(factory.zip),
                }))
              }
              name="zip"
              onChange={onChange}
            />
            <SelectInput
              required
              fullWidth
              native
              disabled={disableForm}
              value={factory.country || ''}
              name="country"
              onChange={onChange}
              label="Country"
              autoComplete="no"
              input={<Input name="country" value={factory.country || ''} />}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  country: isEmpty(factory.country),
                }))
              }
              inputProps={{
                name: `country`,
              }}
              options={CountryList}
            />
          </div>

          <Typography
            variant="h5"
            className={classes.sectionHeader}
            gutterBottom
          >
            Contact Details
          </Typography>
          <Divider />

          <div className={classes.row}>
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Contact Name"
              value={factory.contact_name}
              name="contact_name"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  contact_name: isEmpty(factory.contact_name),
                }))
              }
            />
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Contact Phone"
              value={factory.contact_phone}
              name="contact_phone"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  contact_phone: !isValidPhone(
                    factory.contact_phone,
                    isEmpty(factory.contact_phone),
                  ),
                }))
              }
            />
          </div>
          <div className={classes.row}>
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Contact Email"
              value={factory.contact_email}
              name="contact_email"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  contact_email: !validateEmail(factory.contact_email),
                }))
              }
            />
          </div>
          <div className={classes.row}>
            <TextInput
              fullWidth
              disabled={disableForm}
              label="Garment Registration Number"
              value={factory.garment_registration_number}
              name="garment_registration_number"
              onChange={onChange}
              onBlur={() =>
                setFormErrors((f) => ({
                  ...f,
                  contact_name: isEmpty(factory.garment_registration_number),
                }))
              }
            />

            <div className={classes.checkboxLabelContainer}>
              <FormControlLabel
                classes={{
                  label: classes.checkboxLabel,
                  root: classes.checkboxLabel,
                }}
                label="Preferred Factory"
                labelPlacement="start"
                control={
                  <Checkbox
                    color="primary"
                    disabled={disableForm}
                    checked={!!factory.preferred}
                    onChange={() =>
                      setFactory((f) => ({
                        ...f,
                        preferred: !factory.preferred,
                      }))
                    }
                    name="factory_preferred"
                    label="Preferred Factory"
                  />
                }
              />
            </div>
          </div>

          <div className={classes.row}>
            <FileInput
              suffix="auditDocument"
              onChange={(_, file) => {
                onChangeFile(file, 'auditDocument');
              }}
              fileType={['application/pdf']}
              exists={get(factory, 'auditDocument.exists', false)}
              name="auditDocument"
              disabled={disableForm}
              fileLink={get(factory, 'auditDocument.read', '')}
              header="Factory Audit Compliance Document"
              selectMode={!isUpdateForm}
              progressEvent={props.uploadFileProgress.auditDocument}
            />
          </div>

          <div className={classes.row}>
            <FileInput
              suffix="factoryAgreement"
              onChange={(_, file) => {
                onChangeFile(file, 'factoryAgreement');
              }}
              fileType={['application/pdf']}
              exists={get(factory, 'factoryAgreement.exists', false)}
              defaultFileLink={get(
                props.publicFilesUrls,
                'vendor_factory_agreement.read',
              )}
              name="factoryAgreement"
              disabled={disableForm}
              fileLink={get(factory, 'factoryAgreement.read', '')}
              header="Vendor Factory Agreement"
              selectMode={!isUpdateForm}
              progressEvent={props.uploadFileProgress.factoryAgreement}
            />
          </div>

          <div className={classes.row}>
            <Button color="secondary" onClick={submit} disabled={disableForm}>
              {factory.rejection_reason ? 'Re-Submit' : 'Submit'}
            </Button>
          </div>

          {factory.submitted_at &&
            !factory.rejection_reason &&
            perms.canAcceptReject && (
              <div>
                <FormControl
                  variant="standard"
                  margin="dense"
                  fullWidth
                  className={classes.auditExpirationDate}
                  error={
                    !!factory.audit_expiration_date &&
                    new Date(factory.audit_expiration_date) < new Date()
                  }
                >
                  <InputLabel
                    shrink
                    htmlFor="factory-audit-date"
                    classes={{ root: classes.auditExpirationDateLabel }}
                  >
                    Factory Audit Expiration Date
                  </InputLabel>
                  <Input
                    variant="outlined"
                    name="audit_expiration_date"
                    value={
                      factory.audit_expiration_date
                        ? moment
                            .utc(factory.audit_expiration_date)
                            .format('YYYY-MM-DD')
                        : ''
                    }
                    id="factory-audit-date"
                    onChange={onChange}
                    type="date"
                    disabled={!!factory.approved_by}
                  />
                </FormControl>
                <ApproveReject
                  disableApprove={!factory.audit_expiration_date}
                  onReject={(_, reason) => reject(reason)}
                  record={factory}
                  onApprove={approve}
                  title="Factory"
                />
              </div>
            )}

          {factory.approved_by && (
            <div className={classes.row}>
              <p className={classes.approvedAt}>
                Factory Approved on
                {` ${moment(factory.approved_at).format('LLLL')}`}
              </p>
            </div>
          )}
        </form>
        <Grid item className={classes.error}>
          {error && <div>{error instanceof Error ? error.message : error}</div>}
        </Grid>
        <Grid item className={classes.error}>
          {submitError && <div>{submitError}</div>}
        </Grid>
      </Page.Content>
    </Page.Container>
  );
}

VendorFactory.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      factoryId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  createFactory: PropTypes.func.isRequired,
  updateFactory: PropTypes.func.isRequired,
  getFactory: PropTypes.func.isRequired,
  publicFilesUrls: PropTypes.shape({}).isRequired,
  updateFile: PropTypes.func.isRequired,
  uploadFileProgress: PropTypes.shape({
    auditDocument: PropTypes.shape({}),
    factoryAgreement: PropTypes.shape({}),
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    vendor: PropTypes.shape({
      internal_id: PropTypes.string,
      is_direct: PropTypes.bool,
      merch_type: PropTypes.string,
    }),
  }).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};
